import React, { MutableRefObject, RefCallback } from 'react';

type Ref<T> = ((instance: T | null) => void) | MutableRefObject<T | null> | null;

/**
 * A custom hook that combines multiple refs into a single ref callback.
 * This is useful when you need to pass multiple refs to a single element.
 *
 * @template T - The type of the element being referenced.
 * @param {...Ref<T>[]} refs - The refs to be combined. These can be either callback refs or ref objects.
 * @returns {RefCallback<T>} A ref callback that assigns the element to all provided refs.
 *
 * @example
 * // Usage in a functional component
 * const ref1 = React.useRef(null);
 * const ref2 = React.useRef(null);
 * const combinedRef = useCombinedRefs(ref1, ref2);
 *
 * return <div ref={combinedRef}>Content</div>;
 */
export function useCombinedRefs<T>(...refs: Ref<T>[]): RefCallback<T> {
  return React.useCallback(
    (element) => {
      refs.forEach((ref) => {
        if (!ref) return;
        if (typeof ref === 'function') {
          ref(element);
        } else {
          ref.current = element;
        }
      });
    },
    [refs]
  );
}
